import { useState, useCallback, useEffect, useMemo } from 'react';
import cartAPI from '../services/cartAPI';

// Custom hook for cart management with backend API
const useCart = () => {
  const [cart, setCart] = useState({
    items: [],
    subtotal: 0,
    discount: 0,
    tax: 0,
    total: 0,
    coupon: null
  });
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch cart from backend
  const fetchCart = useCallback(async (addressId = null) => {
    const token = localStorage.getItem('token');
    if (!token) {
      const guestCart = localStorage.getItem('waseeny_guest_cart');
      if (guestCart) {
        try {
          setCart(JSON.parse(guestCart));
        } catch {
          localStorage.removeItem('waseeny_guest_cart');
        }
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Check for guest cart to merge
      const guestCartStr = localStorage.getItem('waseeny_guest_cart');
      if (guestCartStr) {
        try {
          const guestCart = JSON.parse(guestCartStr);
          if (guestCart.items && guestCart.items.length > 0) {
            await cartAPI.mergeCart(guestCart.items);
            localStorage.removeItem('waseeny_guest_cart');
          }
        } catch (e) {
          console.error("Error merging guest cart:", e);
        }
      }

      const data = await cartAPI.getCart(addressId);
      // Backend returns { cart: {...}, bill: {...}, itemCount: ... }
      // Extract the cart object
      setCart(data.cart || { items: [], subtotal: 0, discount: 0, tax: 0, total: 0, coupon: null });
      setBill(data.bill || null);
    } catch (err) {
      setError(err.message || 'Failed to fetch cart');
      console.error('Fetch cart error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Convenience wrapper when you only have the product object
  const addItem = useCallback(async (product) => {
    const restaurantId =
      product.restaurant?._id ||
      product.restaurantId ||
      product.restaurant;
    return addToCart(restaurantId, product._id, 1, null, [], product, false);
  }, []);

  // Add item to cart
  const addToCart = useCallback(async (restaurantId, productId, quantity = 1, variationId = null, addOnsIds = [], fullItem = null, clearCart = false) => {
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      try {
        let newCart;
        setCart(prev => {
          const items = [...prev.items];
          // Guest cart logic: if clearCart is true, start with empty items
          const currentItems = clearCart ? [] : items;
          const existingIndex = currentItems.findIndex(i => i._id === productId || i.productId === productId);

          const updatedItems = [...currentItems];
          if (existingIndex > -1) {
            updatedItems[existingIndex] = {
              ...updatedItems[existingIndex],
              quantity: updatedItems[existingIndex].quantity + quantity
            };
          } else {
            const price = fullItem ? (fullItem.basePrice || fullItem.price || 0) : 0;
            const name = fullItem ? (fullItem.name || 'Item') : 'Item';
            updatedItems.push({ _id: productId, productId, quantity, price, name, fullItem, restaurantId });
          }

          const total = updatedItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
          const newCartData = { ...prev, items: updatedItems, total, restaurantId: clearCart ? restaurantId : (prev.restaurantId || restaurantId) };

          localStorage.setItem('waseeny_guest_cart', JSON.stringify(newCartData));
          return newCartData;
        });
        return { success: true };
      } catch (err) {
        throw err;
      }
    }

    try {
      const data = await cartAPI.addToCart(restaurantId, productId, quantity, variationId, addOnsIds, clearCart);
      // Extract the cart object
      const cartData = data.cart || (data.items ? data : {});
      setCart(cartData);
      setBill(data.bill || null);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to add item to cart');
      console.error('Add to cart error:', err);
      throw err;
    }
  }, []);

  // Remove item from cart
  const removeItem = useCallback(async (itemId) => {
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      let newCart;
      setCart(prev => {
        const items = prev.items.filter(i => i._id !== itemId && i.productId !== itemId);
        const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const newCart = { ...prev, items, total };

        localStorage.setItem('waseeny_guest_cart', JSON.stringify(newCart));
        return newCart;
      });
      return { success: true };
    }

    try {
      const data = await cartAPI.removeItem(itemId);
      // Backend returns { cart: {...}, bill: {...}, itemCount: ... }
      const cartData = data.cart || (data.items ? data : {});
      setCart(cartData);
      setBill(data.bill || null);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to remove item');
      console.error('Remove item error:', err);
      throw err;
    }
  }, []);

  // Update item quantity
  const updateQuantity = useCallback(async (itemId, quantity) => {
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      let newCart;
      setCart(prev => {
        const items = prev.items.map(i =>
          (i._id === itemId || i.productId === itemId)
            ? { ...i, quantity }
            : i
        );
        const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const newCart = { ...prev, items, total };

        localStorage.setItem('waseeny_guest_cart', JSON.stringify(newCart));
        return newCart;
      });
      return { success: true };
    }

    try {
      const data = await cartAPI.updateQuantity(itemId, quantity);
      // Backend returns { cart: {...}, bill: {...}, itemCount: ... }
      const cartData = data.cart || (data.items ? data : {});
      setCart(cartData);
      setBill(data.bill || null);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update quantity');
      console.error('Update quantity error:', err);
      throw err;
    }
  }, []);

  // Update cart meta (address, delivery notes, etc)
  const updateMeta = useCallback(async (meta) => {
    setError(null);
    try {
      const data = await cartAPI.updateMeta(meta);
      // Backend returns { cart: {...}, bill: {...}, itemCount: ... }
      const cartData = data.cart || (data.items ? data : {});
      setCart(cartData);
      setBill(data.bill || null);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update cart');
      console.error('Update meta error:', err);
      throw err;
    }
  }, []);

  // Validate and apply coupon
  const applyCoupon = useCallback(async (code) => {
    setError(null);
    try {
      const data = await cartAPI.validateCoupon(code);
      // Backend returns { cart: {...}, bill: {...}, itemCount: ... }
      if (data.cart) {
        setCart(data.cart);
      }
      setBill(data.bill || null);
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Invalid coupon code';
      setError(errorMessage);
      console.error('Coupon error:', err);
      throw new Error(errorMessage);
    }
  }, []);

  // Derived item count
  const itemCount = useMemo(() => {
    return (cart.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);
  }, [cart.items]);

  // Load cart on component mount; also remove legacy localStorage key
  useEffect(() => {
    localStorage.removeItem('waseeny_cart');
    fetchCart();
  }, [fetchCart]);

  return {
    cart,
    bill,
    loading,
    error,
    itemCount,
    fetchCart,
    addToCart,
    addItem,
    removeItem,
    updateQuantity,
    updateMeta,
    applyCoupon
  };
};

export default useCart;
